import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import ChatbotWidget from "./components/ChatbotWidget";
import Request from "helpers/request";
import { urls } from "helpers/urls";
import CommonNotification from "components/common/CommonNotification";

export default function WidgetFrame() {
  const { siteId } = useParams();
  const [appearanceData, setAppearanceData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const fetchAppearance = async (id) => {
    try {
      const response = await Request.get(urls.getAppearanceUrl(id));
      setAppearanceData(response?.data?.data);
    } catch (error) {
      console.error("Failed to fetch appearance data:", error);
    }
  };

  console.log("sessionId",sessionId)

  const createChatSession = async (siteId) => {
    try {
      const res = await Request.postJson(urls.createChatSession, {
        site_id: siteId,
      });

      const newSessionId = res?.data?.data?.session_id;
      console.log("newSessionId",newSessionId)
      if (newSessionId) {
        setSessionId(newSessionId);
        // Get existing object from localStorage
        const existingSessions =
          JSON.parse(localStorage.getItem("chat_session_id")) || {};

        // Update or add the new site_id: session_id pair
        existingSessions[siteId] = newSessionId;

        // Save back to localStorage
        localStorage.setItem(
          "chat_session_id",
          JSON.stringify(existingSessions)
        );
      }
    } catch (err) {
      console.error("Failed to create chat session:", err);
      return null;
    }
  };

  const fetchChatMessages = async (sessionId, id) => {
    try {
      const response = await Request.get(urls.getChatMessages(sessionId), {
        site_id: id,
      });

      if (response.data && response.data.success) {
        const fetchedMessages = response.data.data.messages || [];

        // Transform API messages to your component's message format
        const transformedMessages = [];

        fetchedMessages.forEach((msg, index) => {
          // Add user message
          const userMessage = {
            id: `user_${msg.id}_${index}`,
            text: msg.user_query,
            type: "user",
            timestamp: new Date(msg.created_at).getTime() || Date.now(),
            sources: [],
          };
          transformedMessages.push(userMessage);

          // Add assistant message
          const assistantMessage = {
            id: msg.id,
            text: msg.assistant_response,
            type: "agent",
            timestamp: new Date(msg.created_at).getTime() || Date.now(),
            sources: msg.sources || [],
            rating: msg.rating,
          };
          transformedMessages.push(assistantMessage);
        });

        setMessages(transformedMessages);

        // NEW: Initialize message states with fetched ratings
        const initialMessageStates = {};
        transformedMessages.forEach((message, index) => {
          if (message.type === "agent" && message.rating !== undefined) {
            initialMessageStates[index] = {
              liked: message.rating === 1,
              disliked: message.rating === -1,
              copied: false,
            };
          } else {
            initialMessageStates[index] = {
              liked: false,
              disliked: false,
              copied: false,
            };
          }
        });

        // You'll need to pass this to ChatbotWidget or handle it there
        // If you have access to setMessageStates here, use it
        // Otherwise, you'll need to modify ChatbotWidget to accept initial states
      }
    } catch (error) {
      console.error("Failed to fetch chat messages:", error);
      CommonNotification.error("Failed to load chat history");
    }
  };

  const checkSessionIdExists = (id) => {
    const stored = localStorage.getItem("chat_session_id");
    if (!stored) {
      createChatSession(id);
    } else {
      const allSessionIds = JSON.parse(stored);
      const currentSessionId = allSessionIds?.[id];

      if (currentSessionId && currentSessionId !== sessionId) {
        console.log("currentSessionId",currentSessionId)
        setSessionId(currentSessionId);
        fetchChatMessages(currentSessionId, id);
      } else {
        createChatSession(id);
      }
    }
  };

  useEffect(() => {
    if (siteId) {
      checkSessionIdExists(siteId);
      fetchAppearance(siteId);
    }
  }, [siteId]);

  const handleSendMessage = async (messageText, animateTyping = null) => {
    if (!messageText.trim() || isLoading) return;

    setIsLoading(true);

    const userMessage = {
      id: `user_${Date.now()}_${Math.random()}`,
      text: messageText,
      type: "user",
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    try {
      const response = await Request.postJson(urls.newChat, {
        site_id: siteId,
        query: messageText,
        session_id: sessionId,
      });

      const apiData = response?.data?.data;
      const botAnswer =
        apiData?.answer || "I couldn't process your request. Please try again.";
      const sources = apiData?.sources || [];
      // FIX: Use message_id instead of assistant_message_id
      const messageId = apiData?.message_id;

      setIsLoading(false);

      if (animateTyping) {
        await animateTyping(botAnswer, messageId, sources);
        const botMessage = {
          // FIX: Use the correct message_id from API response
          id: messageId || `agent_${Date.now()}_${Math.random()}`,
          text: botAnswer,
          type: "agent",
          timestamp: Date.now(),
          sources: sources,
        };
        setMessages((prev) => [...prev, botMessage]);
      } else {
        const botMessage = {
          // FIX: Use the correct message_id from API response
          id: messageId || `agent_${Date.now()}_${Math.random()}`,
          text: botAnswer,
          type: "agent",
          timestamp: Date.now(),
          sources: sources,
        };
        setMessages((prev) => [...prev, botMessage]);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setIsLoading(false);
      CommonNotification.error("Something went wrong. Please try again.");

      const errorMessage = {
        id: `error_${Date.now()}_${Math.random()}`,
        text: "Sorry, I encountered an error while processing your message. Please try again.",
        type: "agent",
        timestamp: Date.now(),
        sources: [],
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleClose = () => {
    setMessages([]);
    setSessionId(null);
  };

  if (siteId)
    return (
      <ChatbotWidget
        siteId={siteId}
        bot_type="widget"
        input={input}
        setInput={setInput}
        setSessionId={setSessionId}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        messagesEndRef={messagesEndRef}
        onCloseMessage={handleClose}
        messages={messages}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onSendMessage={handleSendMessage}
        {...(sessionId && { sessionId: sessionId })}
        {...(appearanceData && {
          widgetPosition: appearanceData.position,
          headerColor: appearanceData.header_color,
          agentMessageColor: appearanceData.agent_message_color,
          agentTextColor: appearanceData.agent_text_color,
          widgetIconColor: appearanceData.icon_color,
          widgetIcon: appearanceData.icon,
        })}
      />
    );
}
